/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EmailConfigForm.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Email configuration form with SMTP settings, authentication,
    and comprehensive email service management.

 🔧 Notes:
    - Implements SMTP configuration with multiple authentication methods
    - Supports SSL/TLS encryption and port configuration
    - Includes Quick List functionality for existing configurations
    - Uses custom tooltips for enhanced UX
    - Implements comprehensive form validation

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, Copy, Search, Mail, Shield, Cpu, LockKeyhole } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { FormBox } from '../../components/ui/FormBox';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { navigateToEmailConfigDuplicate } from './emailSettingsActions';
import { useEmailConfigData } from './hooks/useEmailConfigData';
import {
  useEmailConfigForm,
  AUTH_TYPES,
  AUTH_CREDENTIALS_REQUIRED,
  DEFAULT_FORM_VALUES,
} from './hooks/useEmailConfigForm';
import { useEmailConfigSubmit } from './hooks/useEmailConfigSubmit';
import { useQuickList } from './hooks/useQuickList';
import { useEmailConfigFormFields } from './components/EmailConfigFormFields';
import { mapConfigToFormData, type EmailConfigFormData } from './hooks/useEmailConfigForm';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import type { EmailConfig } from './types';

export function EmailConfigForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { showInfo } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<EmailConfigFormData | null>(null);

  useFixedActionBar(true);

  // Load config data
  const { config, loading: dataLoading } = useEmailConfigData(id, duplicateId, mode, resetParam);

  // Form management
  const form = useEmailConfigForm(config, Boolean(duplicateId));
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    setError,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = form;

  // Submit handler
  const { loading, isDeleting, handleSubmit: handleFormSubmit, handleDelete } = useEmailConfigSubmit(
    id,
    mode,
    (maskedFormValues) => {
      setOriginalFormData(maskedFormValues);
      reset(maskedFormValues);
    }
  );

  // Quick list
  const {
    showQuickList,
    quickListSearch,
    filteredQuickList,
    isQuickListLoading,
    openQuickList,
    closeQuickList,
    setQuickListSearch,
  } = useQuickList();

  const authType = watch('authType');
  const hasChanges = useMemo(() => (mode === 'create' ? true : isDirty), [mode, isDirty]);

  // Form field renderers
  const fieldRenderers = useEmailConfigFormFields({
    register,
    watch,
    setValue,
    clearErrors,
    setError,
    errors,
  });

  // Clear page title

  // Initialize original form data when config loads
  useEffect(() => {
    if (config && !duplicateId) {
      const formData = mapConfigToFormData(config, false);
      setOriginalFormData(formData);
    } else if (!config && !duplicateId && mode === 'create') {
      setOriginalFormData(null);
    }
  }, [config, duplicateId, mode]);

  // Reset form when resetParam changes (Save and New)
  useEffect(() => {
    if (resetParam && mode === 'create') {
      reset(DEFAULT_FORM_VALUES);
      clearErrors();
      setOriginalFormData(null);
    }
  }, [resetParam, mode, reset, clearErrors]);

  // Handle save internal (wraps handleFormSubmit to return EmailConfig)
  const handleSaveInternal = useCallback(async (): Promise<EmailConfig | undefined> => {
      // hasChanges validation removed

    // Get current form values
    const formValues = getValues();
    
    // Call handleFormSubmit directly
    const savedData = await handleFormSubmit(formValues, hasChanges, setError);
    return savedData;
  }, [handleFormSubmit, hasChanges, showInfo, t, mode, id, config, getValues, setError]);

  // Save with action hook
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/admin/system-settings/email',
    newPath: '/admin/system-settings/email/new',
    getEditPath: (savedData) => `/admin/system-settings/email/${savedData.id}`,
    mode,
    storageKey: 'email-settings-save-action',
  });

  const onSubmit = handleSubmit(async (formValues) => {
    await handleFormSubmit(formValues, hasChanges, form.setError);
  });

  const handleCancel = () => {
    reset(originalFormData ?? DEFAULT_FORM_VALUES);
    clearErrors();
  };

  const confirmDelete = async () => {
    await handleDelete();
    setShowDeleteModal(false);
    // Navigate to list after successful delete
    navigate('/admin/system-settings/email');
  };

  const handleDuplicate = () => {
    if (id && id !== 'new') {
      navigateToEmailConfigDuplicate(navigate, id);
    }
  };

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Settings className="h-5 w-5 text-primary" />}
        title={
          mode === 'create'
            ? t('emailSettings.addConfiguration')
            : t('emailSettings.editConfiguration')
        }
        backButtonText={t('common.list')}
        onBack={() => navigate('/admin/system-settings/email')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('emailSettings.quickList') || 'Quick List',
            onClick: openQuickList,
          },
          ...(mode === 'edit' && id && id !== 'new' && !duplicateId
            ? [
                {
                  icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                  tooltip: t('common.duplicate'),
                  onClick: handleDuplicate,
                },
              ]
            : []),
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Basic Settings */}
          <FormBox title={t('emailSettings.basicSettings')} icon={<Mail />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {fieldRenderers.renderInputField('name', {
                labelKey: 'emailSettings.fields.name',
                tooltipKey: 'emailSettings.tooltips.name',
                placeholderKey: 'emailSettings.placeholders.name',
                required: true,
              })}
              {fieldRenderers.renderInputField('fromName', {
                labelKey: 'emailSettings.fields.fromName',
                tooltipKey: 'emailSettings.tooltips.fromName',
                placeholderKey: 'emailSettings.placeholders.fromName',
              })}
              {fieldRenderers.renderInputField('host', {
                labelKey: 'emailSettings.fields.host',
                tooltipKey: 'emailSettings.tooltips.host',
                placeholderKey: 'emailSettings.placeholders.host',
                required: true,
              })}
              {fieldRenderers.renderInputField('replyTo', {
                labelKey: 'emailSettings.fields.replyTo',
                tooltipKey: 'emailSettings.tooltips.replyTo',
                placeholderKey: 'emailSettings.placeholders.replyTo',
                type: 'email',
              })}
              {fieldRenderers.renderInputField('port', {
                labelKey: 'emailSettings.fields.port',
                tooltipKey: 'emailSettings.tooltips.port',
                placeholderKey: 'emailSettings.placeholders.port',
                type: 'number',
                required: true,
                registerOptions: { valueAsNumber: true },
              })}
              {fieldRenderers.renderInputField('fromEmail', {
                labelKey: 'emailSettings.fields.fromEmail',
                tooltipKey: 'emailSettings.tooltips.fromEmail',
                placeholderKey: 'emailSettings.placeholders.fromEmail',
                type: 'email',
                required: true,
              })}
            </div>
          </FormBox>

          {/* Authentication */}
          <FormBox title={t('emailSettings.authentication')} icon={<Shield />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {fieldRenderers.renderSelectField('authType', {
                labelKey: 'emailSettings.fields.authType',
                tooltipKey: 'emailSettings.tooltips.authType',
                options: AUTH_TYPES,
              })}
              {fieldRenderers.renderToggleField('secure', {
                labelKey: 'emailSettings.fields.secure',
                tooltipKey: 'emailSettings.tooltips.secure',
              })}
              {fieldRenderers.renderToggleField('tls', {
                labelKey: 'emailSettings.fields.tls',
                tooltipKey: 'emailSettings.tooltips.tls',
              })}
            </div>

            {authType !== 'none' && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {fieldRenderers.renderInputField('username', {
                  labelKey: 'emailSettings.fields.username',
                  tooltipKey: 'emailSettings.tooltips.username',
                  placeholderKey: 'emailSettings.placeholders.username',
                  required: true,
                })}
                {fieldRenderers.renderPasswordField()}
              </div>
            )}
          </FormBox>

          {/* Email Settings */}
          <FormBox title={t('emailSettings.emailSettings')} icon={<LockKeyhole />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {fieldRenderers.renderToggleField('isActive', {
                labelKey: 'emailSettings.fields.enabled',
                tooltipKey: 'emailSettings.tooltips.enabled',
              })}
              {fieldRenderers.renderToggleField('isDefault', {
                labelKey: 'emailSettings.fields.isDefault',
                tooltipKey: 'emailSettings.tooltips.isDefault',
              })}
            </div>
          </FormBox>

          {/* Advanced Settings */}
          <FormBox
            title={t('emailSettings.advancedSettings')}
            icon={<Cpu />}
            collapsible
            defaultCollapsed={false}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {fieldRenderers.renderInputField('connectionTimeout', {
                labelKey: 'emailSettings.fields.connectionTimeout',
                tooltipKey: 'emailSettings.tooltips.connectionTimeout',
                placeholderKey: 'emailSettings.placeholders.connectionTimeout',
                type: 'number',
                registerOptions: { valueAsNumber: true },
              })}
              {fieldRenderers.renderInputField('greetingTimeout', {
                labelKey: 'emailSettings.fields.greetingTimeout',
                tooltipKey: 'emailSettings.tooltips.greetingTimeout',
                placeholderKey: 'emailSettings.placeholders.greetingTimeout',
                type: 'number',
                registerOptions: { valueAsNumber: true },
              })}
              {fieldRenderers.renderInputField('socketTimeout', {
                labelKey: 'emailSettings.fields.socketTimeout',
                tooltipKey: 'emailSettings.tooltips.socketTimeout',
                placeholderKey: 'emailSettings.placeholders.socketTimeout',
                type: 'number',
                registerOptions: { valueAsNumber: true },
              })}
            </div>
          </FormBox>
        </form>
      </div>

      {/* Barra de acciones fija */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={mode === 'edit' ? () => setShowDeleteModal(true) : undefined}
        isEditMode={mode === 'edit'}
        saveText={loading ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        saveDisabled={loading || dataLoading}
        isLoading={loading}
        hasChanges={hasChanges}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="email-settings-save-action"
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={closeQuickList}
        title={t('emailSettings.quickListTitle', 'Quick List - Select Email Configuration')}
        items={filteredQuickList.filter((c): c is typeof c & { id: string } => !!c.id)}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(config) => {
          closeQuickList();
          navigate(`/admin/system-settings/email/${config.id}`);
        }}
        currentItemId={mode === 'edit' && !duplicateId ? id : null}
        renderItem={(config, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{config.name}</p>
              <p className="text-sm text-muted-foreground">
                {config.host}:{config.port}
              </p>
              <p className="text-sm text-muted-foreground">{config.fromEmail}</p>
              {config.isDefault && (
                <p className="text-xs font-medium text-primary mt-1">
                  {t('emailSettings.quickListDefault', 'Default')}
                </p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('emailSettings.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t(
          'emailSettings.quickListSearchPlaceholder',
          'Search by name, host, email or port'
        )}
        emptyMessage={t('emailSettings.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('emailSettings.quickListEmptyWithSearch', 'No configurations found')}
        headerIcon={<Mail className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('emailSettings.messages.deleteConfirmTitle')}
        message={t('emailSettings.messages.deleteConfirmMessage')}
        confirmText={t('emailSettings.messages.deleteConfirm')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
